import { useQuery } from '@apollo/client';
// Api
import { ESTIMATE_STREAM_PRICE } from 'api/streams/queries';
// Types
import {
  EstimateStreamPrice,
  EstimateStreamPriceVariables,
} from 'api/streams/types/EstimateStreamPrice';
// Helpers
import { calculateAffiliateCommission, roundMoney } from 'helpers/affiliate';

/**
 * What the organizer keeps from one PPV sale after the platform fee.
 * Returns 0 for free streams and while the price is empty or invalid.
 */
export const useEstimatedStreamProfit = (
  price: string | number | null | undefined,
  isFree?: boolean
): number => {
  const finalPrice = Number(price);
  const skip = Boolean(isFree) || !(finalPrice > 0);

  const { data } = useQuery<EstimateStreamPrice, EstimateStreamPriceVariables>(
    ESTIMATE_STREAM_PRICE,
    {
      variables: { input: { finalPrice } },
      skip,
    }
  );

  if (skip) {
    return 0;
  }

  return data?.estimateStreamPrice.profit ?? 0;
};

type StreamEarningsValues = {
  price: string | number | null | undefined;
  isFree: boolean;
  affiliateEnabled?: boolean;
  affiliateCommissionPercent?: string;
};

/** The PPV net of one sale, and what the organizer keeps of it once a participant's commission is paid. */
export const useStreamEarnings = ({
  price,
  isFree,
  affiliateEnabled,
  affiliateCommissionPercent,
}: StreamEarningsValues): { netProfit: number; youEarn: number } => {
  const netProfit = useEstimatedStreamProfit(price, isFree);
  const commission =
    affiliateEnabled && !isFree
      ? calculateAffiliateCommission(
          netProfit,
          Number(affiliateCommissionPercent)
        )
      : 0;

  return { netProfit, youEarn: roundMoney(netProfit - commission) };
};
